import { ref } from 'vue'
import { defineStore } from 'pinia'

import { BaseError, camelCaseKeysToUnderscore, underscoreKeysToCamelCase } from '@ezio/utils'
import { useEzioAPI } from '@ezio/services'

import {
    CatalogOverrides,
    ManagerPermissions,
    ManagerPermissionsUpdate,
    PermissionCatalog,
    PermissionTemplate,
    SparsePermissions
} from './types'

/**
 * Routes API consommées :
 *   GET  /v1/permission_catalog                 (?agency_id= | ?raw=true)
 *   GET  /v1/managers/:id/permissions
 *   PUT  /v1/managers/:id/permissions        body: { global?, overrides? }
 *
 * Ces endpoints ne sont pas au format JSON:API : le deserializer les laisse
 * tels quels (snake_case). C'est donc `response.data` qu'on convertit en
 * camelCase. Les clés numériques (id d'agence type "42") ne sont pas touchées
 * par les converters de @ezio/utils.
 */
export const usePermissionsStore = defineStore('permissions', () => {
    const { api } = useEzioAPI()

    const catalog = ref<PermissionCatalog>(null)
    // le catalogue porte des libellés adaptés à l'agence : cache par agence
    const catalogAgencyId = ref<string>(null)

    // raw : libellés bruts (placeholders non résolus), pour l'éditeur du catalogue
    const fetchCatalog = async (
        agencyId?: string | number,
        options?: { raw?: boolean }
    ): Promise<PermissionCatalog> => {
        const raw = !!options?.raw
        const key = raw ? '#raw' : agencyId != null ? String(agencyId) : null
        if (catalog.value && catalogAgencyId.value === key) {
            return catalog.value
        }
        try {
            const response = await api.get(
                'permission_catalog',
                raw ? { raw: true } : key ? { agency_id: key } : {}
            )
            catalog.value = underscoreKeysToCamelCase(response.data) as PermissionCatalog
            catalogAgencyId.value = key
            return catalog.value
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const fetchFor = async (managerId: string | number): Promise<ManagerPermissions> => {
        try {
            const response = await api.get('managers/' + managerId + '/permissions')
            return underscoreKeysToCamelCase(response.data) as ManagerPermissions
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const update = async (
        managerId: string | number,
        payload: ManagerPermissionsUpdate
    ): Promise<ManagerPermissions> => {
        try {
            const body = camelCaseKeysToUnderscore(payload)
            const response = await api.put('managers/' + managerId + '/permissions', body)
            return underscoreKeysToCamelCase(response.data) as ManagerPermissions
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    /**
     * Templates (permission_templates) : CRUD réservé managers compte + super
     * admins côté backend. apply copie le JSON du template dans le niveau visé.
     */
    const fetchTemplates = async (): Promise<PermissionTemplate[]> => {
        try {
            // pagination: false — sans lui l'API renvoie une réponse à cursor,
            // que Http.onSuccess laisse brute (non désérialisée)
            const response = await api.get('permission_templates', { pagination: false })
            const body: any = response.data
            // selon l'endpoint : tableau désérialisé, JSON:API brut (réponse à
            // cursor : data = [{ id, type, attributes }]), ou racine enveloppée
            const list: any[] = Array.isArray(body)
                ? body
                : Array.isArray(body?.data)
                  ? body.data.map((r: any) =>
                        r.attributes ? Object.assign({ id: r.id }, r.attributes) : r
                    )
                  : (body?.permission_templates ?? body?.permissionTemplates ?? [])
            // underscoreKeysToCamelCase ne gère pas les tableaux : conversion par élément
            return list.map((t: any) => underscoreKeysToCamelCase(t)) as PermissionTemplate[]
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const createTemplate = async (template: {
        name: string
        description?: string
        permissions: SparsePermissions
    }): Promise<PermissionTemplate> => {
        try {
            const response = await api.post('permission_templates', camelCaseKeysToUnderscore(template))
            return underscoreKeysToCamelCase(response.data) as PermissionTemplate
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const deleteTemplate = async (templateId: number): Promise<void> => {
        try {
            await api.delete('permission_templates/' + templateId)
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const applyTemplate = async (
        templateId: number,
        target: { userId: string | number; agencyId?: string | number }
    ): Promise<ManagerPermissions> => {
        try {
            const body: any = { user_id: target.userId }
            if (target.agencyId !== undefined) {
                body.agency_id = target.agencyId
            }
            const response = await api.post('permission_templates/' + templateId + '/apply', body)
            return underscoreKeysToCamelCase(response.data) as ManagerPermissions
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    /**
     * Personnalisation d'affichage du catalogue (super admin uniquement) :
     * libellés et positions, rien d'autre. Le PUT remplace intégralement le
     * sparse stocké ; le catalogue servi ensuite intègre ces personnalisations.
     */
    const fetchCatalogOverrides = async (): Promise<CatalogOverrides> => {
        try {
            const response = await api.get('permission_catalog/overrides')
            const body = underscoreKeysToCamelCase(response.data) as { overrides: CatalogOverrides }
            return body?.overrides ?? {}
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const updateCatalogOverrides = async (
        overrides: CatalogOverrides
    ): Promise<CatalogOverrides> => {
        try {
            const response = await api.put(
                'permission_catalog/overrides',
                camelCaseKeysToUnderscore({ overrides })
            )
            // les libellés/positions servis par le catalogue viennent de changer
            catalog.value = null
            catalogAgencyId.value = null
            const body = underscoreKeysToCamelCase(response.data) as { overrides: CatalogOverrides }
            return body?.overrides ?? {}
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const reset = () => {
        catalog.value = null
        catalogAgencyId.value = null
    }

    return {
        catalog,
        fetchCatalog,
        fetchFor,
        update,
        fetchTemplates,
        createTemplate,
        deleteTemplate,
        applyTemplate,
        fetchCatalogOverrides,
        updateCatalogOverrides,
        reset
    }
})
